import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Check, 
  X,
  Clock,
  AlertCircle,
  Pencil,
  Ban,
  Calendar,
  Info,
  Users,
  ChevronDown,
  CalendarDays
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr, de, enUS } from 'date-fns/locale';
import LeavePlanningModal from '../components/LeavePlanningModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Absences() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const locale = language === 'de' ? de : language === 'en' ? enUS : fr;
  
  const [absences, setAbsences] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedUsers, setSelectedUsers] = useState([]); // Multiple selection
  const [userFilterOpen, setUserFilterOpen] = useState(false);
  const [newAbsence, setNewAbsence] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    absence_type: 'conge',
    day_part: 'full',
    for_user_id: ''  // For managers to submit for others
  });
  const [editAbsence, setEditAbsence] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    absence_type: 'conge',
    day_part: 'full'
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [planningModalOpen, setPlanningModalOpen] = useState(false);
  
  // Working days calculation for new absence form
  const [workingDaysInfo, setWorkingDaysInfo] = useState(null);
  const [loadingWorkingDays, setLoadingWorkingDays] = useState(false);
  
  // Cache for working days calculations in the table
  const [absencesDaysCache, setAbsencesDaysCache] = useState({});

  const isManager = user?.role === 'manager' || user?.role === 'directeur';
  const isDirecteur = user?.role === 'directeur';  // Only directeurs can edit/approve absences

  // Absence types based on role
  const ABSENCE_TYPES_ALL = [
    { value: 'conge', label: t('paidLeave') },
    { value: 'maladie', label: t('sickLeave') },
    { value: 'sans_solde', label: t('unpaidLeave') },
  ];
  
  const ABSENCE_TYPES_COLLABORATOR = [
    { value: 'conge', label: t('paidLeave') },
  ];
  
  // Get available types based on role
  const getAvailableAbsenceTypes = () => {
    return isManager ? ABSENCE_TYPES_ALL : ABSENCE_TYPES_COLLABORATOR;
  };

  const DAY_PARTS = [
    { value: 'full', label: t('fullDay') },
    { value: 'morning', label: t('morning') },
    { value: 'afternoon', label: t('afternoon') },
  ];

  const STATUS_LABELS = {
    pending: { label: t('pending'), class: 'bg-amber-500' },
    approved: { label: t('approved'), class: 'bg-emerald-500' },
    rejected: { label: t('rejected'), class: 'bg-red-500' },
    cancelled: { label: t('cancelled'), class: 'bg-gray-500' },
  };

  // Fetch users list for filter (managers only)
  useEffect(() => {
    if (isManager) {
      axios.get(`${API}/users`).then(res => {
        setUsers(res.data || []);
      }).catch(() => {});
    }
  }, [isManager]);

  // Calculate working days when dates change in the form
  const calculateWorkingDays = useCallback(async (startDate, endDate, dayPart) => {
    if (!startDate || !endDate) {
      setWorkingDaysInfo(null);
      return;
    }
    
    setLoadingWorkingDays(true);
    try {
      const res = await axios.get(`${API}/holidays/working-days`, {
        params: { start_date: startDate, end_date: endDate, day_part: dayPart }
      });
      setWorkingDaysInfo(res.data);
    } catch (err) {
      console.error('Error calculating working days:', err);
      setWorkingDaysInfo(null);
    } finally {
      setLoadingWorkingDays(false);
    }
  }, []);

  // Update working days calculation when form changes
  useEffect(() => {
    if (dialogOpen && newAbsence.start_date && newAbsence.end_date) {
      calculateWorkingDays(newAbsence.start_date, newAbsence.end_date, newAbsence.day_part);
    }
  }, [dialogOpen, newAbsence.start_date, newAbsence.end_date, newAbsence.day_part, calculateWorkingDays]);

  // Fetch working days for absences in the table
  // SECURITY FIX: Removed absencesDaysCache from dependencies to prevent infinite loop
  const fetchWorkingDaysForAbsences = useCallback(async (absencesList) => {
    setAbsencesDaysCache(prevCache => {
      // Check which absences need fetching (not in cache)
      const toFetch = absencesList.filter(a => !prevCache[a.id]);
      
      if (toFetch.length === 0) return prevCache;
      
      // Fetch data for uncached absences
      Promise.all(
        toFetch.map(async (absence) => {
          try {
            const res = await axios.get(`${API}/holidays/working-days`, {
              params: { 
                start_date: absence.start_date, 
                end_date: absence.end_date, 
                day_part: absence.day_part || 'full' 
              }
            });
            return { id: absence.id, data: res.data };
          } catch (err) {
            return { id: absence.id, data: null };
          }
        })
      ).then(results => {
        setAbsencesDaysCache(currentCache => {
          const newCache = { ...currentCache };
          results.forEach(({ id, data }) => {
            if (data) newCache[id] = data;
          });
          return newCache;
        });
      });
      
      return prevCache;
    });
  }, []); // Empty dependencies - no infinite loop

  const fetchAbsences = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      // For multiple user selection, we'll filter client-side
      const res = await axios.get(`${API}/absences`, { params });
      let filteredAbsences = res.data;
      
      // Apply user filter if any users are selected
      if (isManager && selectedUsers.length > 0) {
        filteredAbsences = res.data.filter(a => selectedUsers.includes(a.user_id));
      }
      
      setAbsences(filteredAbsences);
      
      // Fetch working days for all absences
      if (filteredAbsences.length > 0) {
        fetchWorkingDaysForAbsences(filteredAbsences);
      }
    } catch (err) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, selectedUsers, isManager, t, fetchWorkingDaysForAbsences]);

  useEffect(() => {
    fetchAbsences();
  }, [fetchAbsences]);

  const handleCreateAbsence = async () => {
    try {
      // Prepare absence data
      const absenceData = {
        start_date: newAbsence.start_date,
        end_date: newAbsence.end_date,
        reason: newAbsence.reason,
        absence_type: newAbsence.absence_type,
        day_part: newAbsence.day_part
      };
      
      // If manager is creating for another user
      if (isManager && newAbsence.for_user_id) {
        absenceData.for_user_id = newAbsence.for_user_id;
      }
      
      await axios.post(`${API}/absences`, absenceData);
      toast.success(t('requestCreated'));
      setDialogOpen(false);
      setNewAbsence({
        start_date: '',
        end_date: '',
        reason: '',
        absence_type: 'conge',
        day_part: 'full',
        for_user_id: ''
      });
      setWorkingDaysInfo(null);
      fetchAbsences();
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
    }
  };

  const handleUpdateAbsence = async () => {
    if (!selectedAbsence) return;
    try {
      await axios.put(`${API}/absences/${selectedAbsence.id}`, editAbsence);
      toast.success(t('success'));
      setEditDialogOpen(false);
      setSelectedAbsence(null);
      // Clear cache for this absence to refetch
      const newCache = { ...absencesDaysCache };
      delete newCache[selectedAbsence.id];
      setAbsencesDaysCache(newCache);
      fetchAbsences();
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
    }
  };

  const openEditAbsenceDialog = (absence) => {
    setSelectedAbsence(absence);
    setEditAbsence({
      start_date: absence.start_date,
      end_date: absence.end_date,
      reason: absence.reason,
      absence_type: absence.absence_type,
      day_part: absence.day_part || 'full'
    });
    setEditDialogOpen(true);
  };

  const handleReview = async (status) => {
    if (!selectedAbsence) return;
    
    try {
      await axios.put(`${API}/absences/${selectedAbsence.id}/review`, {
        status,
        rejection_reason: status === 'rejected' ? rejectionReason : (status === 'cancelled' ? t('cancelledByAdmin') : null)
      });
      const messages = {
        approved: t('requestApproved'),
        rejected: t('requestRejected'),
        cancelled: t('requestCancelled')
      };
      toast.success(messages[status] || t('success'));
      setReviewDialogOpen(false);
      setSelectedAbsence(null);
      setRejectionReason('');
      fetchAbsences();
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
    }
  };

  const handleCancelAbsence = async (absence) => {
    if (!window.confirm(t('confirmCancelAbsence'))) return;
    
    try {
      await axios.put(`${API}/absences/${absence.id}/review`, {
        status: 'cancelled',
        rejection_reason: t('cancelledByAdmin')
      });
      toast.success(t('requestCancelled'));
      fetchAbsences();
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
    }
  };

  const handleDeleteAbsence = async (id) => {
    try {
      await axios.delete(`${API}/absences/${id}`);
      toast.success(t('success'));
      fetchAbsences();
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
    }
  };

  const openReviewDialog = (absence) => {
    setSelectedAbsence(absence);
    setReviewDialogOpen(true);
  };

  // Calculate calendar days for display
  const getCalendarDays = (absence) => {
    if (!absence.start_date || !absence.end_date) return '-';
    const start = new Date(absence.start_date);
    const end = new Date(absence.end_date);
    const diffTime = Math.abs(end - start);
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    if (absence.day_part && absence.day_part !== 'full' && absence.start_date === absence.end_date) {
      return '0.5';
    }
    return days.toString();
  };

  // Get working days from cache
  const getWorkingDays = (absence) => {
    const cached = absencesDaysCache[absence.id];
    if (cached) {
      return cached.effective_days.toString();
    }
    // Fallback estimate while loading
    if (!absence.start_date || !absence.end_date) return '-';
    
    if (absence.day_part && absence.day_part !== 'full' && absence.start_date === absence.end_date) {
      return '0.5';
    }
    
    let workingDays = 0;
    let current = new Date(absence.start_date);
    const end = new Date(absence.end_date);
    while (current <= end) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
      current.setDate(current.getDate() + 1);
    }
    return `~${workingDays}`;
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Select/deselect all users
  const toggleAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const pendingCount = absences.filter(a => a.status === 'pending').length;

  // Get label for user filter button
  const getUserFilterLabel = () => {
    if (selectedUsers.length === 0) {
      return t('allCollaborators');
    }
    if (selectedUsers.length === 1) {
      const u = users.find(u => u.id === selectedUsers[0]);
      return u ? `${u.first_name} ${u.last_name}` : '1 sélectionné';
    }
    return `${selectedUsers.length} sélectionnés`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="absences-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl">
            {isManager ? t('absenceRequests').toUpperCase() : t('myAbsences').toUpperCase()}
          </h1>
          <p className="text-muted-foreground text-sm">
            {pendingCount} {t('requestsPending')}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Planning congés button */}
          <Button 
            variant="outline" 
            onClick={() => setPlanningModalOpen(true)}
            data-testid="planning-conges-btn"
          >
            <CalendarDays className="h-4 w-4 mr-2" />
            {language === 'de' ? 'Ferienplaner' : language === 'en' ? 'Leave Planner' : 'Planning congés'}
          </Button>
          
          {/* Collaborator filter with multiple selection (managers only) */}
          {isManager && (
            <Popover open={userFilterOpen} onOpenChange={setUserFilterOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-[200px] justify-between"
                  data-testid="user-filter"
                >
                  <span className="flex items-center gap-2 truncate">
                    <Users className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{getUserFilterLabel()}</span>
                  </span>
                  <ChevronDown className="h-4 w-4 flex-shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[250px] p-0" align="start">
                <div className="p-2 border-b border-border">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    onClick={toggleAllUsers}
                  >
                    <Checkbox 
                      checked={selectedUsers.length === users.length && users.length > 0}
                      className="mr-2"
                    />
                    {selectedUsers.length === users.length ? t('deselectAll') : t('selectAll')}
                  </Button>
                </div>
                <div className="max-h-[300px] overflow-y-auto p-2">
                  {users.map(u => (
                    <div
                      key={u.id}
                      className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                      onClick={() => toggleUserSelection(u.id)}
                    >
                      <Checkbox 
                        checked={selectedUsers.includes(u.id)}
                        onCheckedChange={() => toggleUserSelection(u.id)}
                      />
                      <span className="text-sm">{u.first_name} {u.last_name}</span>
                    </div>
                  ))}
                </div>
                {selectedUsers.length > 0 && (
                  <div className="p-2 border-t border-border">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => setSelectedUsers([])}
                    >
                      {t('clearSelection')}
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          )}
          
          {/* Status filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('allStatuses')}</SelectItem>
              <SelectItem value="pending">{t('pending')}</SelectItem>
              <SelectItem value="approved">{t('approved')}</SelectItem>
              <SelectItem value="rejected">{t('rejected')}</SelectItem>
              <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
            </SelectContent>
          </Select>
          
          {/* New absence button - visible for collaborateurs and directeurs, but NOT managers */}
          {(user?.role === 'collaborateur' || isDirecteur) && (
          <Button onClick={() => setDialogOpen(true)} data-testid="new-absence-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t('newRequest')}
          </Button>
          )}
        </div>
      </div>

      {/* Absences List */}
      {absences.length === 0 ? (
        <div className="border border-border rounded-lg p-8 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t('noAbsences')}</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                {isManager && <th>{t('collaborator')}</th>}
                <th>{t('type')}</th>
                <th>{t('period')}</th>
                <th>{t('dayPart')}</th>
                <th className="text-center">{t('realDays')}</th>
                <th className="text-center">{t('deductedDays')}</th>
                <th>{t('reason')}</th>
                <th>{t('status')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {absences.map((absence) => (
                <tr key={absence.id} data-testid={`absence-row-${absence.id}`}>
                  {isManager && (
                    <td className="font-medium">{absence.user_name}</td>
                  )}
                  <td>
                    {getAvailableAbsenceTypes().find(t => t.value === absence.absence_type)?.label || 
                     ABSENCE_TYPES_ALL.find(t => t.value === absence.absence_type)?.label || 
                     absence.absence_type}
                  </td>
                  <td className="mono text-sm">
                    {format(parseISO(absence.start_date), 'dd/MM/yyyy', { locale })}
                    {absence.start_date !== absence.end_date && (
                      <> → {format(parseISO(absence.end_date), 'dd/MM/yyyy', { locale })}</>
                    )}
                  </td>
                  <td className="text-sm">
                    {DAY_PARTS.find(p => p.value === (absence.day_part || 'full'))?.label || t('fullDay')}
                  </td>
                  <td className="text-sm text-center text-muted-foreground">
                    {getCalendarDays(absence)}j
                  </td>
                  <td className="text-sm font-medium text-center text-primary">
                    {getWorkingDays(absence)}j
                  </td>
                  <td className="max-w-[200px] truncate">{absence.reason}</td>
                  <td>
                    <Badge className={`${STATUS_LABELS[absence.status].class} text-white rounded`}>
                      {STATUS_LABELS[absence.status].label}
                    </Badge>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      {isDirecteur && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditAbsenceDialog(absence)}
                          className="h-8 px-2"
                          data-testid={`edit-btn-${absence.id}`}
                          title={t('edit')}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {isDirecteur && absence.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openReviewDialog(absence)}
                          className="h-8 px-2"
                          data-testid={`review-btn-${absence.id}`}
                        >
                          <Clock className="h-4 w-4" />
                        </Button>
                      )}
                      {isDirecteur && absence.status === 'approved' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCancelAbsence(absence)}
                          className="h-8 px-2 text-orange-600 hover:text-orange-700"
                          data-testid={`cancel-btn-${absence.id}`}
                          title={t('cancel')}
                        >
                          <Ban className="h-4 w-4" />
                        </Button>
                      )}
                      {absence.user_id === user?.id && absence.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteAbsence(absence.id)}
                          className="h-8 px-2 text-destructive hover:text-destructive"
                          data-testid={`delete-btn-${absence.id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Absence Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) setWorkingDaysInfo(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('newAbsenceRequest').toUpperCase()}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* Collaborator selection (managers only) */}
            {isManager && (
              <div className="space-y-2">
                <Label>{t('forCollaborator')}</Label>
                <Select
                  value={newAbsence.for_user_id || 'myself'}
                  onValueChange={(v) => setNewAbsence({ ...newAbsence, for_user_id: v === 'myself' ? '' : v })}
                >
                  <SelectTrigger data-testid="absence-for-user">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="myself">{t('forMyself')}</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('absenceType')}</Label>
              <Select
                value={newAbsence.absence_type}
                onValueChange={(v) => setNewAbsence({ ...newAbsence, absence_type: v })}
              >
                <SelectTrigger data-testid="absence-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableAbsenceTypes().map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('startDate')}</Label>
                <Input
                  type="date"
                  value={newAbsence.start_date}
                  onChange={(e) => setNewAbsence({ ...newAbsence, start_date: e.target.value, end_date: e.target.value })}
                  data-testid="absence-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('endDate')}</Label>
                <Input
                  type="date"
                  value={newAbsence.end_date}
                  onChange={(e) => setNewAbsence({ ...newAbsence, end_date: e.target.value })}
                  data-testid="absence-end-date"
                />
              </div>
            </div>

            {/* Day part selection - only show if single day */}
            {newAbsence.start_date && newAbsence.start_date === newAbsence.end_date && (
              <div className="space-y-2">
                <Label>{t('dayPart')}</Label>
                <Select
                  value={newAbsence.day_part}
                  onValueChange={(v) => setNewAbsence({ ...newAbsence, day_part: v })}
                >
                  <SelectTrigger data-testid="absence-day-part">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAY_PARTS.map(part => (
                      <SelectItem key={part.value} value={part.value}>
                        {part.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Working days calculation display */}
            {(newAbsence.start_date && newAbsence.end_date) && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-2">
                  <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-900 dark:text-blue-100">
                      {t('effectiveDeduction')}
                    </p>
                    {loadingWorkingDays ? (
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        {t('calculating')}...
                      </p>
                    ) : workingDaysInfo ? (
                      <div className="text-sm text-blue-700 dark:text-blue-300 mt-1 space-y-1">
                        <p className="text-lg font-bold">
                          {workingDaysInfo.effective_days} {t('days')}
                        </p>
                        <p className="text-xs">
                          {t('realDays')}: {workingDaysInfo.calendar_days}j • 
                          {t('deductedDays')}: {workingDaysInfo.working_days}j
                        </p>
                        {workingDaysInfo.excluded_days.length > 0 && (
                          <p className="text-xs flex items-center gap-1">
                            <Info className="h-3 w-3" />
                            {language === 'de' 
                              ? `Ausgeschlossen: ${workingDaysInfo.excluded_days.filter(d => d.reason === 'weekend').length} Wochenende(n), ${workingDaysInfo.excluded_days.filter(d => d.reason === 'holiday').length} Feiertag(e)`
                              : language === 'en'
                              ? `Excluded: ${workingDaysInfo.excluded_days.filter(d => d.reason === 'weekend').length} weekend(s), ${workingDaysInfo.excluded_days.filter(d => d.reason === 'holiday').length} holiday(s)`
                              : `Exclus : ${workingDaysInfo.excluded_days.filter(d => d.reason === 'weekend').length} week-end(s), ${workingDaysInfo.excluded_days.filter(d => d.reason === 'holiday').length} jour(s) férié(s)`
                            }
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('reason')}</Label>
              <Textarea
                value={newAbsence.reason}
                onChange={(e) => setNewAbsence({ ...newAbsence, reason: e.target.value })}
                placeholder={t('reasonPlaceholder')}
                data-testid="absence-reason"
              />
            </div>

            <Button onClick={handleCreateAbsence} className="w-full" data-testid="submit-absence-btn">
              {t('submitRequest')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('processRequest').toUpperCase()}</DialogTitle>
          </DialogHeader>
          
          {selectedAbsence && (
            <div className="space-y-4 pt-4">
              <div className="border border-border rounded-lg p-4 space-y-2 bg-muted/30">
                <p><span className="text-muted-foreground">{t('collaborator')}:</span> {selectedAbsence.user_name}</p>
                <p><span className="text-muted-foreground">{t('type')}:</span> {ABSENCE_TYPES_ALL.find(t => t.value === selectedAbsence.absence_type)?.label}</p>
                <p>
                  <span className="text-muted-foreground">{t('period')}:</span>{' '}
                  {format(parseISO(selectedAbsence.start_date), 'dd/MM/yyyy')} → {format(parseISO(selectedAbsence.end_date), 'dd/MM/yyyy')}
                </p>
                <p>
                  <span className="text-muted-foreground">{t('realDays')}:</span> {getCalendarDays(selectedAbsence)}j
                  {' • '}
                  <span className="text-muted-foreground">{t('deductedDays')}:</span> <span className="font-bold text-primary">{getWorkingDays(selectedAbsence)}j</span>
                </p>
                <p><span className="text-muted-foreground">{t('reason')}:</span> {selectedAbsence.reason}</p>
              </div>

              <div className="space-y-2">
                <Label>{t('rejectionReason')}</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder={t('rejectionPlaceholder')}
                  data-testid="rejection-reason"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleReview('approved')}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="approve-btn"
                >
                  <Check className="h-4 w-4 mr-2" />
                  {t('approve')}
                </Button>
                <Button
                  onClick={() => handleReview('rejected')}
                  variant="destructive"
                  className="flex-1"
                  data-testid="reject-btn"
                >
                  <X className="h-4 w-4 mr-2" />
                  {t('reject')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Absence Dialog (Admin only) */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('edit').toUpperCase()} - {t('absenceRequests')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>{t('absenceType')}</Label>
              <Select
                value={editAbsence.absence_type}
                onValueChange={(v) => setEditAbsence({ ...editAbsence, absence_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ABSENCE_TYPES_ALL.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('startDate')}</Label>
                <Input
                  type="date"
                  value={editAbsence.start_date}
                  onChange={(e) => setEditAbsence({ ...editAbsence, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('endDate')}</Label>
                <Input
                  type="date"
                  value={editAbsence.end_date}
                  onChange={(e) => setEditAbsence({ ...editAbsence, end_date: e.target.value })}
                />
              </div>
            </div>

            {editAbsence.start_date && editAbsence.start_date === editAbsence.end_date && (
              <div className="space-y-2">
                <Label>{t('dayPart')}</Label>
                <Select
                  value={editAbsence.day_part}
                  onValueChange={(v) => setEditAbsence({ ...editAbsence, day_part: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAY_PARTS.map(part => (
                      <SelectItem key={part.value} value={part.value}>
                        {part.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('reason')}</Label>
              <Textarea
                value={editAbsence.reason}
                onChange={(e) => setEditAbsence({ ...editAbsence, reason: e.target.value })}
              />
            </div>

            <Button onClick={handleUpdateAbsence} className="w-full">
              {t('update')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Leave Planning Modal */}
      <LeavePlanningModal 
        open={planningModalOpen} 
        onClose={() => setPlanningModalOpen(false)} 
      />
    </div>
  );
}
